import { Component, effect, input, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { InputLabel } from '../../parts/input-label/input-label.component';
import { EmailInput } from '../../parts/email-input/email-input.component';
import { PhoneInput } from '../../parts/phone-input/phone-input.component';
import { CustomValidators } from '../../core/validators/custom-validators';
import { OrganizationContactModel, CreateOrganizationContactRequest } from '../../core/models/data/organization-contact.model';
import { OrganizationContactType } from '../../core/enums/data.enums';

@Component({
  selector: 'ms-organization-contact-form',
  standalone: true,
  imports: [ReactiveFormsModule, FaDuotoneIconComponent, InputLabel, EmailInput, PhoneInput],
  templateUrl: './organization-contact.form.html'
})
export class OrganizationContactForm extends BaseFormComponent {

  public contactType = input.required<OrganizationContactType>();
  public existingContact = input<OrganizationContactModel | undefined>(undefined);

  public cancelRequested = output<void>();
  public backRequested = output<void>();
  public submitted = output<CreateOrganizationContactRequest>();

  public contactForm: FormGroup;

  constructor(private fb: FormBuilder) {
    super();
    this.contactForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [CustomValidators.email]],
      telephone: [{}, [CustomValidators.phone]]
    });

    effect(() => {
      const existing = this.existingContact();
      if (existing) {
        this.contactForm.patchValue({
          name: existing.name ?? '',
          email: existing.emailAddress ?? '',
          telephone: {
            countryCode: existing.phoneCountryCode ?? '1',
            phoneNumber: existing.phoneNumber ?? '',
            extension: existing.phoneExtension ?? ''
          }
        });
      } else {
        this.contactForm.reset({
          name: '',
          email: '',
          telephone: {}
        });
      }
    });
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public onBack(): void {
    this.backRequested.emit();
  }

  public onSubmit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const telephone = this.contactForm.value.telephone ?? {};
    const request: CreateOrganizationContactRequest = {
      type: this.contactType(),
      name: this.contactForm.value.name,
      emailAddress: this.contactForm.value.email || undefined,
      phoneCountryCode: telephone.phoneNumber ? telephone.countryCode : undefined,
      phoneNumber: telephone.phoneNumber || undefined,
      phoneExtension: telephone.extension || undefined
    };

    this.submitted.emit(request);
  }

  public resetForm(): void {
    this.contactForm.reset({
      name: '',
      email: '',
      telephone: {}
    });
  }
}
